import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';
import type { CommandHandler } from 'utils/commands/CommandHandler';

export type CommandHandlerFactory<TCommandContext, TCommandArgs = never> = (
  context: TCommandContext,
  dispatch: CommandDispatcher<TCommandContext>,
) => CommandHandler<TCommandArgs>;

export type CommandDispatcher<TCommandContext> = {
  // dispatch command with optional argument
  <TCommandArgs>(
    makeCommandHandler: undefined extends TCommandArgs
      ? CommandHandlerFactory<TCommandContext, TCommandArgs>
      : never,
    args?: TCommandArgs,
  ): Awaitable<void>;
  // dispatch command with mandatory argument
  <TCommandArgs>(
    makeCommandHandler: CommandHandlerFactory<TCommandContext, TCommandArgs>,
    args: TCommandArgs,
  ): Awaitable<void>;
  // dispatch command with no argument
  (makeCommandHandler: CommandHandlerFactory<TCommandContext>): Awaitable<void>;
};

/**
 * Provide a simple way to call commands with "command dependency context"
 *
 * The goal is to provide a reducer-like tool with minimum verbosity.
 * To create a new command, you just add an appropriately typed command handler factory function,
 * then call it through provided dispatch mechanism. No need to register the command into
 * any global reducer or so.
 *
 * Command handlers are passed in a form of 2nd order "handler factory" functions:
 * (context, dispatch) => (argument?) => Awaitable<void>
 *
 * The dependency context provides stuff like state hook access, API methods etc.
 * The command itself can optionally accept an argument object containing actual command parameters
 * like entity IDs, form values etc.
 *
 * We recommend to memoize the command context argument. This hook caches the result of
 * handler factory, which can be furthermore used for caching stuff inside these factories.
 *
 * Example:
 * ```
 * // command dispatcher setup (in some top-level component)
 * interface UserTableCommandContext {
 *   getState: () => UserState;
 *   updateState: (s: UserState) => void;
 * }
 * const cmdContext: UserTableCommandContext = useMemo(...somehow obtain context dependencies...);
 * const dispatch = useCommandDispatcher(cmdContext);
 *
 * // command definition
 * interface AddUserCommand {
 *   name: string;
 *   age: number;
 * }
 * // note: for commands without arguments, omit the second generic parameter.
 * // for optional parameters, add undefined to the command type parameter, e.g. `AddUserCommand | undefined`.
 * const addUser: CommandHandlerFactory<UserTableCommandContext, AddUserCommand> = ({updateState}, dispatch) =>
 *   ({name, age}) => {
 *     updateState(s => {...s, users: [...s.users, {name, age}]});
 *     dispatch(...); // call other dispatchable commands from this one
 *   };
 *
 * // usage (dispatch retrieved from top via React context or props)
 * dispatch(addUser, {name: 'John Doe', age: 18});
 * ```
 */
export const useCommandDispatcher = <TCommandContext,>(
  context: TCommandContext,
): CommandDispatcher<TCommandContext> => {
  // biome-ignore lint/correctness/useExhaustiveDependencies(context): intentional, revalidate command cache on changes in context
  const commandCache = useMemo(
    () =>
      new Map<
        CommandHandlerFactory<TCommandContext, any>,
        CommandHandler<any>
      >(),
    [context],
  );
  const dispatch: CommandDispatcher<TCommandContext> = useStableCallback(
    (
      makeCommandHandler: CommandHandlerFactory<TCommandContext, any>,
      ...args: [argObject?: any]
    ) => {
      const handleCommand = pipe(
        commandCache,
        plainMap.getOrCreate(makeCommandHandler, () =>
          makeCommandHandler(context, dispatch),
        ),
      );
      if (args.length >= 1) {
        return handleCommand(args[0]);
      }

      return handleCommand();
    },
  );
  return dispatch;
};

/**
 * Create curried dispatcher
 *
 * @example
 * // raw call
 * curryDispatch(dispatch)(deleteUser)({userId: 1});
 * // useful for pointfree calls:
 * pipe(
 *  userId,
 *  userId => ({userId}),
 *  curryDispatch(dispatch)(deleteUser),
 * );
 */
export const curryDispatch =
  <TCommandContext,>(dispatch: CommandDispatcher<TCommandContext>) =>
  <TCommandArgs,>(
    makeCommandHandler: CommandHandlerFactory<TCommandContext, TCommandArgs>,
  ) =>
  (args: TCommandArgs): Awaitable<void> =>
    dispatch(makeCommandHandler, args);
